// The playtest (docs/05-Tools/Level Designer.md, Playtest): the level
// designer embeds the game on /playtest and posts an authoring record to it;
// the game plays it locally, Home becomes Quit (a message back), and Reset
// starts the same level afresh. Messages are plain objects with a `type`
// prefixed wits-playtest; the page acts only on a well-formed level and
// answers the sender, so an unexpected embedder gains nothing but a game of
// its own level.
import { parseAuthoringLevel, type AuthoringLevel } from '@wits/core';
import { setPlayBackend } from './backend';
import { createLocalBackend } from './localPlay';
import { closePlay, openPlay } from './playSession';

export const PLAYTEST_PATH = '/playtest';

export type PlaytestMessage = { type: 'wits-playtest-ready' } | { type: 'wits-playtest-quit' } | { type: 'wits-playtest-level'; level: unknown };

let backend: ReturnType<typeof createLocalBackend> | null = null;
let current: AuthoringLevel | null = null;

export function isPlaytest(): boolean {
  return typeof location !== 'undefined' && location.pathname.replace(/\/$/, '') === PLAYTEST_PATH;
}

/** Take a level from the designer and start playing it. Returns the problem, if the record is not a level. */
export function startPlaytest(raw: unknown): string | null {
  let level: AuthoringLevel;
  try {
    level = parseAuthoringLevel(raw);
  } catch (e) {
    return (e as Error).message.split('\n')[0] ?? 'not a level';
  }
  current = level;
  backend = createLocalBackend(level);
  setPlayBackend(backend);
  closePlay();
  // The play screen opens the play when it mounts for the level.
  return null;
}

/** Reset: the same level, a fresh board, a fresh clock. */
export function restartPlaytest(): void {
  if (!backend || !current) return;
  backend.reset();
  closePlay();
  void openPlay(current.id);
}

export function endPlaytest(): void {
  setPlayBackend(null);
  backend = null;
  current = null;
  closePlay();
}

/** Tell the designer we are done. */
export function quitPlaytest(): void {
  window.parent?.postMessage({ type: 'wits-playtest-quit' } satisfies PlaytestMessage, '*');
}
